import { useEffect, useMemo, useRef, KeyboardEvent } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// material-ui
import { styled, useTheme, Theme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import Header from './Header';
import Sidebar from './Sidebar';
import HorizontalBar from './HorizontalBar';

import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';
import { useFocusMainContent } from 'hooks/useFocusMainContent';

interface MainStyleProps {
    theme: Theme;
    open: boolean;
    layout: string;
}

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open, layout }: MainStyleProps) => ({
    ...theme.typography.mainContent,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: theme.palette.grey[100],
    ...(!open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: layout === LAYOUT_CONST.VERTICAL_LAYOUT ? -(drawerWidth - 72) : '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? 135 : 80
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginTop: 80
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginRight: '10px',
            marginTop: 80
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter
        }),
        marginLeft: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? '20px' : 0,
        marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? 135 : 80,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.up('md')]: {
            marginTop: layout === LAYOUT_CONST.HORIZONTAL_LAYOUT ? 135 : 80
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            marginTop: 80
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            marginTop: 80
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();

    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch();
    const { drawerOpen, selectedItem, selectedID } = useSelector((state) => state.menu);
    const { drawerType, container, layout } = useConfig();
    const { pathname } = useLocation();

    // Gestión del foco para la accesibilidad
    const focusMainContent = useFocusMainContent();
    const mainRef = useRef<HTMLDivElement>(null);
    const firstRender = useRef(true);

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        } else {
            dispatch(openDrawer(false));
        }
    }, [drawerType]);

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        }
    }, []);

    useEffect(() => {
        if (matchDownMd) {
            dispatch(openDrawer(true));
        }
    }, [matchDownMd]);

    // Gestión del foco al cambiar de ruta
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        focusMainContent();
    }, [pathname, focusMainContent]);

    const condition = layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd;

    const header = useMemo(
        () => (
            <Toolbar sx={{ p: condition ? '10px' : '16px' }}>
                <Header />
            </Toolbar>
        ),
        [layout, matchDownMd]
    );

    // Gestión de navegación inversa (Shift+Tab) hacia el elemento de menú activo
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.shiftKey && event.key === 'Tab') {
            const firstFocusable = mainRef.current?.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );

            if (document.activeElement === mainRef.current || document.activeElement === firstFocusable) {
                let targetElement: HTMLElement | null = null;

                if (selectedItem && selectedItem.length > 0) {
                    targetElement = document.getElementById(`menu-item-${selectedItem[0]}`);
                }

                if (!targetElement && selectedID) {
                    targetElement = document.getElementById(`menu-item-${selectedID}`);
                }

                if (!targetElement) {
                    targetElement = document.querySelector('.Mui-selected') as HTMLElement;
                }

                if (pathname.includes('/config/templates')) {
                    const headerBtn = document.getElementById('header-btn-templates');
                    if (headerBtn) targetElement = headerBtn;
                } else if (pathname.includes('/config/developers')) {
                    const headerBtn = document.getElementById('header-btn-developers');
                    if (headerBtn) targetElement = headerBtn;
                } else if (pathname.includes('/config/certificates')) {
                    const headerBtn = document.getElementById('header-btn-certificates');
                    if (headerBtn) targetElement = headerBtn;
                }

                if (targetElement) {
                    event.preventDefault();
                    targetElement.focus();
                }
            }
        }
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {/* header */}
            <AppBar
                component="header"
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{ bgcolor: theme.palette.background.default }}
            >
                {header}
            </AppBar>

            {/* horizontal menu-list bar */}
            {layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd && <HorizontalBar />}

            {/* drawer */}
            {(layout === LAYOUT_CONST.VERTICAL_LAYOUT || matchDownMd) && <Sidebar />}

            {/* main content */}
            <Main theme={theme} open={drawerOpen} layout={layout} ref={mainRef} tabIndex={0} onKeyDown={handleKeyDown} id="main-content">
                <Container maxWidth={container ? 'lg' : false} {...(!container && { sx: { px: { xs: 0 } } })}>
                    <Outlet />
                </Container>
            </Main>
        </Box>
    );
};

export default MainLayout;
